// Listen-in panel for a live call. Audio is a passive tap (see
// apps/server/src/services/live-monitor), so neither the agent nor the
// prospect can hear the supervisor or notice any change to the call.
// Everything received is kept locally so the listener can rewind, then
// jump back to live.

import { useEffect, useRef, useState } from 'react';
import type { MonitorAudioPayload, MonitorEndedPayload } from '@marnic/shared/events';
import { formatDuration } from '@marnic/shared';
import { useSocket } from '../lib/socket';
import { MonitorPlayer, type MonitorPlayerState } from '../lib/monitor-player';

type Status = 'connecting' | 'listening' | 'ended' | 'error';

const ENDED_REASONS: Record<string, string> = {
  call_ended: 'Call ended',
  stream_closed: 'Call ended',
  no_audio: 'No audio received from the call',
  audio_stalled: 'Audio stopped arriving',
  idle: 'Monitor closed',
  tap_failed: 'Could not tap the call',
  rtp_error: 'Audio connection failed',
};

export function LiveMonitorPanel({
  callId,
  title,
  subtitle,
  onClose,
}: {
  callId: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
}): JSX.Element {
  const socket = useSocket();
  const playerRef = useRef<MonitorPlayer | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<MonitorPlayerState | null>(null);
  const [volume, setVolume] = useState(1);
  const [scrubbing, setScrubbing] = useState<number | null>(null);
  const endedRef = useRef(false);

  // One player per monitored call; it outlives socket reconnects so the
  // rewind history survives a token refresh.
  useEffect(() => {
    const player = new MonitorPlayer();
    playerRef.current = player;
    endedRef.current = false;
    setStatus('connecting');
    setMessage(null);
    void player.resume();
    const t = setInterval(() => setState(player.getState()), 200);
    return () => {
      clearInterval(t);
      player.destroy();
      playerRef.current = null;
    };
  }, [callId]);

  useEffect(() => {
    if (!socket) return;

    const onAudio = (p: MonitorAudioPayload): void => {
      if (p.callId !== callId) return;
      const bytes = p.data instanceof Uint8Array ? p.data : new Uint8Array(p.data);
      playerRef.current?.append(p.offset, bytes);
    };
    const onEnded = (p: MonitorEndedPayload): void => {
      if (p.callId !== callId) return;
      endedRef.current = true;
      setStatus('ended');
      setMessage(ENDED_REASONS[p.reason] ?? `Monitor stopped (${p.reason})`);
    };
    const subscribe = (): void => {
      if (endedRef.current) return;
      socket.emit('monitor_subscribe', { callId }, (res) => {
        if (res.ok) {
          setStatus('listening');
          setMessage(null);
        } else {
          endedRef.current = true;
          setStatus('error');
          setMessage(res.detail ?? res.error);
        }
      });
    };

    socket.on('monitor_audio', onAudio);
    socket.on('monitor_ended', onEnded);
    socket.on('connect', subscribe);
    if (socket.connected) subscribe();
    return () => {
      socket.off('monitor_audio', onAudio);
      socket.off('monitor_ended', onEnded);
      socket.off('connect', subscribe);
      if (socket.connected) socket.emit('monitor_unsubscribe', { callId });
    };
  }, [socket, callId]);

  useEffect(() => {
    playerRef.current?.setVolume(volume);
  }, [volume]);

  const player = playerRef.current;
  const buffered = state?.bufferedSeconds ?? 0;
  const position = scrubbing ?? state?.positionSeconds ?? 0;
  const hasAudio = buffered > 0;
  const isLive = !!state?.isLive && status === 'listening';

  const act = (fn: (p: MonitorPlayer) => void) => () => {
    const p = playerRef.current;
    if (!p) return;
    void p.resume();
    fn(p);
    setState(p.getState());
  };

  return (
    <div className="card border-brand-300 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusPill status={status} isLive={isLive} behind={state?.behindSeconds ?? 0} paused={!!state?.paused} />
            <h3 className="truncate text-sm font-semibold">{title}</h3>
          </div>
          {subtitle && <p className="mt-0.5 truncate text-xs text-ink-500">{subtitle}</p>}
        </div>
        <button type="button" className="btn-ghost -mr-2 -mt-1 px-2 py-1 text-xs" onClick={onClose} aria-label="Stop listening">
          ✕ Close
        </button>
      </div>

      {message && (
        <p className={`mt-3 rounded-md px-3 py-2 text-xs ${status === 'error' ? 'bg-red-50 text-red-700' : 'bg-ink-50 text-ink-600'}`}>
          {message}
          {status === 'ended' && hasAudio && ' — you can still replay what was captured.'}
        </p>
      )}

      <div className="mt-4">
        <input
          type="range"
          className="w-full accent-brand-600"
          min={0}
          max={Math.max(buffered, 0.1)}
          step={0.1}
          value={Math.min(position, buffered)}
          disabled={!hasAudio}
          aria-label="Playback position"
          onChange={(e) => setScrubbing(Number(e.target.value))}
          onPointerUp={() => {
            if (scrubbing !== null) player?.seek(scrubbing);
            setScrubbing(null);
          }}
          onKeyUp={() => {
            if (scrubbing !== null) player?.seek(scrubbing);
            setScrubbing(null);
          }}
        />
        <div className="mt-1 flex justify-between font-mono text-xs tabular-nums text-ink-500">
          <span>{formatDuration(Math.floor(position))}</span>
          <span>{formatDuration(Math.floor(buffered))}</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={!hasAudio} onClick={act((p) => p.skip(-30))}>« 30s</button>
        <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={!hasAudio} onClick={act((p) => p.skip(-10))}>‹ 10s</button>
        <button
          type="button"
          className="btn-secondary w-20 px-2 py-1 text-xs"
          disabled={!hasAudio}
          onClick={act((p) => p.setPaused(!p.getState().paused))}
        >
          {state?.paused ? '▶ Play' : '❚❚ Pause'}
        </button>
        <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={!hasAudio || isLive} onClick={act((p) => p.skip(10))}>10s ›</button>
        <button
          type="button"
          className={`${isLive ? 'btn-secondary' : 'btn-primary'} px-3 py-1 text-xs`}
          disabled={!hasAudio || isLive || status !== 'listening'}
          onClick={act((p) => p.goLive())}
        >
          ● Go live
        </button>

        <div className="ml-auto flex items-center gap-2">
          <LevelMeter level={state?.level ?? 0} />
          <input
            type="range"
            className="w-20 accent-brand-600"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            aria-label="Volume"
            onChange={(e) => setVolume(Number(e.target.value))}
          />
        </div>
      </div>

      <p className="mt-3 text-[11px] text-ink-400">
        Listen-only. The agent and caller can't hear you and the call isn't affected. Listening is logged in the audit trail.
      </p>
    </div>
  );
}

function StatusPill({ status, isLive, behind, paused }: { status: Status; isLive: boolean; behind: number; paused: boolean }): JSX.Element {
  if (status === 'connecting') return <span className="badge bg-ink-100 text-ink-600">Connecting…</span>;
  if (status === 'error') return <span className="badge bg-red-100 text-red-700">Unavailable</span>;
  if (status === 'ended') return <span className="badge bg-ink-100 text-ink-600">Ended</span>;
  if (paused) return <span className="badge bg-amber-100 text-amber-700">Paused · −{formatDuration(Math.floor(behind))}</span>;
  if (isLive) {
    return (
      <span className="badge gap-1.5 bg-red-100 text-red-700">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" />
        LIVE
      </span>
    );
  }
  return <span className="badge bg-amber-100 text-amber-700">−{formatDuration(Math.floor(behind))} behind</span>;
}

function LevelMeter({ level }: { level: number }): JSX.Element {
  // Perceptual-ish scaling so normal speech moves the bar visibly.
  const pct = Math.min(100, Math.round(Math.sqrt(level) * 100));
  return (
    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-100" aria-hidden>
      <div className="h-full bg-emerald-500 transition-[width] duration-150" style={{ width: `${pct}%` }} />
    </div>
  );
}
